import React from 'react';
import { WeezHeader, Button, Card, ContentCard, FilterSetController, Table, TableCellDropdown, ButtonDropdown, Dropdown, Icon, ToastContext, Message } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import LoadingTable from '../../../../components/loadingTable';
import { Inventory, Zone, Category } from '../../../../models';
import { useHistory } from 'react-router-dom';
import ZoneDeletionModal from './components/ZoneDeletionModal';
import SeatMapModal from './components/seatmap/SeatMapModal';
import Contingent from '../../../../models/contingentModel';
import { fetcher } from '@weezevent/weezjs-core';
import css from './zones.module.css';
import Chart from '../../../../models/chart';

export default function Zones({ organization, chartId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [search, setSearch] = React.useState('');

    const [zones, loadingZones, sync] = Zone.useApiModel(
        {
            organizationId: organization.id,
            chartId: chartId,
            cache: false,
            query: {
                withSeats: true
            }
        },
        [organization, chartId]
    );

    const [categories] = Category.useApiModel(
        {
            organizationId: organization.id,
            chartId: chartId
        },
        [organization, chartId]
    );

    const [contingents] = Contingent.useApiModel(
        {
            organizationId: organization.id,
            chartId
        },
        [organization, chartId]
    );

    const [inventories] = Inventory.useApiModel(
        {
            organizationId: organization.id,
            chartId: chartId
        },
        [organization, chartId]
    );

    const handleBack = React.useCallback(() => {
        history.push(`/ticket/O${organization.id}/charts/${chartId}/dashboard`);
    }, [history, organization, chartId]);

    const handleNew = React.useCallback(() => {
        history.push(`/ticket/O${organization.id}/charts/${chartId}/zones/new`);
    }, [history, organization, chartId]);

    const getPacifaFile = React.useCallback(() => {
        fetcher
            .get(`/ticket/organizations/${organization.pk}/charts/${chartId}/download_pacifa_file`)
            .then(({ response }) => {
                window.open(response.url, '_blank');
            })
            .catch(e => {
                console.log(e);
                toast.error(t('ticket.ticket_option.module.cgv.error'));
            });
    }, [chartId, organization]);

    const handleSearch = React.useCallback((synth, value) => {
        if (!value) {
            return setSearch('');
        }
        setSearch(value);
    });

    return (
        <>
            <WeezHeader
                backLink={{
                    onClick: () => handleBack()
                }}
                title={t('ticket.charts.chart.zones')}
                rightComponent={[
                    <Button key="display" label={t('ticket.charts.chart.zones_add')} primary onClick={handleNew} />,
                    <ButtonDropdown
                        key="dropdown"
                        trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                        items={[<Dropdown.Item key="export_pacifa" onClick={() => getPacifaFile()} item={t('ticket.charts.chart.download_pacifa_file')} />]}
                    />
                ]}
            />
            <WarningAboutNestedZonesInPacifa zones={zones} organization={organization} chartId={chartId} />
            <Card>
                <ContentCard className={css['zones-filterset']}>
                    <FilterSetController
                        total={zones?.length}
                        isLoadingTotal={loadingZones}
                        labelTotal={t('zone', { count: zones?.length })}
                        onChange={() => {}}
                        search={{
                            onChange: (synth, value) => {
                                handleSearch(synth, value);
                            },
                            handleReset: () => {
                                handleSearch();
                            },
                            placeholder: t('ticket.charts.chart.searchZone'),
                            value: search
                        }}
                    />
                </ContentCard>
                <>
                    <Table useNew>
                        <Table.Header>
                            <Table.Tr>
                                <th className={`${css['width-x-large']}`}>{t('ticket.chart.zone')}</th>
                                <th className={`${css['width-x-small']}`}>{t('ticket.chart.capacity')}</th>
                                <th className={`${css['width-small']}`}>{t('ticket.common.type')}</th>
                                <th className={`${css['width-x-mini']}`}>{t('ticket.manager.tableHeader.actions')}</th>
                            </Table.Tr>
                        </Table.Header>
                        {!loadingZones ? (
                            <>
                                {zones?.length > 0 ? (
                                    <Table.TBody>
                                        <ZoneTable
                                            sync={sync}
                                            zones={zones}
                                            query={search}
                                            chartId={chartId}
                                            handleNew={handleNew}
                                            loading={loadingZones}
                                            categories={categories}
                                            contingents={contingents}
                                            inventories={inventories}
                                            organization={organization}
                                        />
                                    </Table.TBody>
                                ) : (
                                    <Table.Tr>
                                        <td colSpan={4}>
                                            <Card.EmptyState
                                                onClick={handleNew}
                                                icon={<Icon name="list" />}
                                                labelButton={t('ticket.charts.chart.zones_add')}
                                                title={t('ticket.charts.chart.noZone')}
                                                subtitle={t('ticket.charts.chart.empty_zone_description')}
                                            />
                                        </td>
                                    </Table.Tr>
                                )}
                            </>
                        ) : (
                            <LoadingTable loading={loadingZones} />
                        )}
                    </Table>
                </>
            </Card>
        </>
    );
}

export function ZoneTable({ zones, query, loading, sync, categories, contingents, inventories, organization, chartId, handleNew }) {
    const { t } = useTranslation();
    const sort_by_display_oder = React.useCallback((zone1, zone2) => {
        if (!zone1.display_order) {
            return -1;
        }

        if (!zone2.display_order) {
            return 1;
        }

        return zone1.display_order - zone2.display_order;
    }, []);

    const zonesTree = React.useMemo(() => {
        const _zones = [...zones];
        _zones?.map(zone => {
            if (!zone.parent) {
                return;
            }

            let parent_zone = zones.find(z => z.pk === zone.parent);
            parent_zone.children = parent_zone.children || [];
            let has_child = parent_zone.children.find(z => z.pk === zone.pk);
            if (!has_child) {
                parent_zone.children.push(zone);
            }
            return parent_zone;
        });
        return _zones;
    }, [zones]);

    const matchSearch = React.useCallback((value, search) => {
        const reg = new RegExp(search, 'gi');
        return reg.test(value);
    });

    const filterNodeHierarchy = React.useCallback(
        (node, search) => {
            if (matchSearch(node.name, search)) {
                return node;
            }

            if (!node.children) {
                return null;
            }

            let filteredNodeTree = { ...node };
            filteredNodeTree.children = node.children.map(node => filterNodeHierarchy(node, search)).filter(Boolean);

            if (!filteredNodeTree.children.length) {
                return null;
            }
            return filteredNodeTree.object;
        },
        [matchSearch]
    );

    const filteredParentZones = React.useMemo(() => {
        /* Since Pyvar can't provide us with zones as a hierarchy tree, filtering and searching
        are recursively processed here.
        */
        let zones = zonesTree?.map(node => filterNodeHierarchy(node, query)).filter(Boolean);

        return zones?.filter(zone => !zone.parent).sort(sort_by_display_oder);
    }, [query, zonesTree, sort_by_display_oder, filterNodeHierarchy]);

    if (!filteredParentZones?.length) {
        return (
            <Card.EmptyState
                className={css['no-zones']}
                icon={<Icon name="list" />}
                onClick={() => handleNew()}
                title={t('ticket.charts.zone.empty-search')}
                labelButton={t('ticket.charts.chart.zones_add')}
            />
        );
    }

    return filteredParentZones.map(zn => (
        <TableRow
            zone={zn}
            key={zn.uuid}
            sync={sync}
            loading={loading}
            chartId={chartId}
            categories={categories}
            contingents={contingents}
            inventories={inventories}
            organization={organization}
            sort_by_display_oder={sort_by_display_oder}
        />
    ));
}

export function TableRow({ depth = 0, organization, chartId, zone, loading, categories, contingents, inventories, sync, sort_by_display_oder }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [showChildren, setShowChildren] = React.useState(true);
    const [toggleModal, setToggleModal] = React.useState(false);
    const [togglePreview, setTogglePreview] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    const childrenZones = React.useMemo(() => {
        if (!zone?.children) {
            return null;
        }

        return zone.children.sort(sort_by_display_oder).map(child => {
            return (
                <TableRow
                    zone={child}
                    key={child.uuid}
                    depth={depth + 1}
                    sync={sync}
                    chartId={chartId}
                    categories={categories}
                    contingents={contingents}
                    inventories={inventories}
                    organization={organization}
                />
            );
        });
    }, [zone]);

    const zoneCapacity = React.useMemo(() => {
        if (!zone.general_admission) {
            return zone.seat_count || null;
        }
        return zone.general_admission.capacity || null;
    }, [zone]);

    const zoneCategoryColor = React.useMemo(() => {
        if (!zone.general_admission || !categories?.length) {
            return 'transparent';
        }
        let category = categories?.find(cat => cat.uuid === zone.general_admission.category);
        if (category) {
            return category.color;
        }
        return 'transparent';
    }, [zone, categories]);

    const zoneType = React.useMemo(() => {
        if (!zone.general_admission) {
            return t('ticket.common.seating.numberedSeating');
        }
        return t('ticket.common.seating.freeSeating');
    }, [zone]);

    const tableHeader = React.useMemo(() => {
        if (depth === 0 && zone.children?.length) {
            return css['zone-group'];
        }
        if (depth === 1) {
            return css['zone-sub-group'];
        }
        if (!zone.parent) {
            return css['zone-standalone'];
        }
    }, [zone]);

    const zoneLabel = React.useMemo(() => {
        if (!zone.parent && !zone.children?.length) {
            return zone.name;
        }
        if (!zone.parent) {
            return (
                <>
                    <Icon name="folder-open" onClick={() => setShowChildren(!showChildren)} /> {zone.name}
                </>
            );
        }
        if (zone.parent && zone.children?.length) {
            return (
                <>
                    <Icon name="link-group" size="small" onClick={() => setShowChildren(!showChildren)} />
                    <Icon name="folder-open" onClick={() => setShowChildren(!showChildren)} />
                    {zone.name}
                </>
            );
        }
        if (zone.parent && !zone.children?.length) {
            return (
                <span className={css['standalone-icon-margin']}>
                    <Icon className={css['standalone-icon']} name="link-group" size="small" />
                    {zone.name}
                </span>
            );
        }
    }, [zone, showChildren]);

    const handleDelete = React.useCallback(
        selectedInventories => {
            setSubmitting(true);
            zone.delete(
                {
                    organizationId: organization.id,
                    chartId
                },
                { inventories: selectedInventories }
            )
                .then(() => {
                    setToggleModal(false);
                    setSubmitting(false);
                    toast.success(t('ticket.charts.categories.delete_success'));
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.charts.categories.delete_error'));
                })
                .finally(() => {
                    sync();
                });
        },
        [zone, organization, chartId, sync]
    );

    const handleToggle = React.useCallback(() => {
        setTogglePreview(!togglePreview);
    }, [togglePreview]);

    return (
        <>
            <ZoneDeletionModal
                zone={toggleModal && zone}
                onClose={() => setToggleModal(false)}
                onSubmit={handleDelete}
                organizationId={organization.id}
                chartId={chartId}
                submitting={submitting}
            />
            <Table.Tr
                className={tableHeader}
                onClick={() => {
                    return history.push(`/ticket/O${organization.id}/charts/${chartId}/zones/${zone.uuid}`);
                }}
            >
                <Table.Td className={css['width-x-large']}>
                    <span style={{ marginLeft: `${23 * depth}px` }}>{zoneLabel}</span>
                </Table.Td>
                <Table.Td className={css['width-x-small']}>{zoneCapacity}</Table.Td>
                <Table.Td className={css['width-small']}>
                    <span className={css['category-type']}>
                        {zoneType}
                        <div className={css['category-dot']} style={{ backgroundColor: zoneCategoryColor }} />
                    </span>
                </Table.Td>
                <Table.Td className={`${css['width-x-mini']} ${css['actions-block']}`}>
                    {<Icon className={zone.seats.length < 1 && css['preview-disabled']} name="eye" onClick={() => setTogglePreview(!togglePreview)} />}
                    <TableCellDropdown>
                        <Dropdown.Item
                            item={t('ticket.common.action.edit')}
                            onClick={() => {
                                return history.push(`/ticket/O${organization.id}/charts/${chartId}/zones/${zone.uuid}`);
                            }}
                        />
                        <Dropdown.Line />
                        <Dropdown.Item item={t('ticket.common.action.delete')} deleteItem onClick={() => setToggleModal(true)} />
                    </TableCellDropdown>
                </Table.Td>
            </Table.Tr>
            {showChildren && childrenZones}
            {togglePreview && (
                <SeatMapModal
                    toggle={togglePreview}
                    onToggle={handleToggle}
                    zone={zone}
                    organization={organization}
                    chartId={chartId}
                    categories={categories?.filter(cat => !cat.general_admission)}
                    contingents={contingents}
                    loadingzones={loading}
                    synczones={sync}
                />
            )}
        </>
    );
}

export function WarningAboutNestedZonesInPacifa({ zones, organization, chartId }) {
    const { t } = useTranslation();
    const [chart] = Chart.useApiModel(
        {
            id: chartId,
            organizationId: organization.id,
            launch: Boolean(chartId)
        },
        [chartId, organization]
    );
    if (!zones?.find(zone => zone.parent)) return null;
    if (!chart?.pacifa_key) return null;
    return (
        <Message type="warning" title={t('ticket.charts.zone.warning_3d_nested_zones.title')}>
            {t('ticket.charts.zone.warning_3d_nested_zones.text')}
        </Message>
    );
}
